import axios from 'axios';
import { authService } from './authService';
import { showSessionExpiredGlobal } from '../contexts/SessionExpirationContext';
import { resolveApiBaseUrl } from '../config/api';

const API_URL = resolveApiBaseUrl();
if (import.meta.env.DEV) {
  console.log('[CWY Admin] API:', API_URL, '| Socket:', API_URL.replace(/\/api\/?$/, ''));
}

/** Avoid noise in console for expected 400/409s handled in UI (e.g. duplicate client on create/update). */
function shouldSuppressResponseErrorLog(err: {
  config?: { url?: string; method?: string };
  response?: { status?: number; data?: { error?: string; message?: string } };
}): boolean {
  const url = err.config?.url || '';
  if (url.includes('/nav-stats/')) return true;

  const method = (err.config?.method || '').toLowerCase();
  const status = err.response?.status;
  const d = err.response?.data;
  if (!d || typeof d !== 'object') return false;

  if (url.includes('createclient') && status === 400) {
    if (d.error === 'Client already exists') return true;
    const m = String(d.message || '').toLowerCase();
    return m.includes('already exists') || m.includes('client with this email');
  }

  if (method === 'patch' && /\/clients\/[^/]+$/.test(url) && status === 409) {
    if (d.error === 'Client already exists') return true;
    const m = String(d.message || '').toLowerCase();
    return m.includes('already exists');
  }

  if (method === 'get' && status === 404 && /\/photo-jobs\/[^/]+\/(selection|submission)$/.test(url)) {
    return true; // Expected when checking if they exist
  }

  return false;
}

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-CWY-Client': 'admin-web',
  }
});

// Add request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Default instance uses Content-Type: application/json; that breaks multipart uploads.
    // Let the browser set multipart boundary when body is FormData.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    console.log('Response received:', response.status);
    return response;
  },
  async (error) => {
    if (!shouldSuppressResponseErrorLog(error)) {
      console.error('Response error:', error.response?.status, error.response?.data);
    }
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh token yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Don't try to logout if the request was already a logout request or a ping request
      if (originalRequest.url && 
          !originalRequest.url.includes('/auth/logout') && 
          !originalRequest.url.includes('/auth/ping') &&
          !originalRequest.url.includes('/nav-stats/')) {
        try {
          // Get the error message from the response
          // Prefer "Session expired due to inactivity" message, otherwise use the response message
          const responseMessage = String(error.response?.data?.message || '');
          const isSessionExpiry =
            responseMessage.includes('Session expired') ||
            responseMessage.includes('inactivity') ||
            error.response?.data?.code === 'TOKEN_EXPIRED';

          if (!isSessionExpiry) {
            // Wrong token type / auth mismatch — not an idle admin session.
            return Promise.reject(error);
          }

          const errorMessage =
            responseMessage ||
            'Your session has expired due to inactivity. Please login again to continue.';

          // Clear token from localStorage without making API call
          localStorage.removeItem('token');
          authService.token = null;

          // Only show modal and redirect if we're not already on the login page
          if (!window.location.pathname.includes('/login')) {
            showSessionExpiredGlobal(errorMessage);
          }
        } catch (refreshError) {
          console.error('Error during logout:', refreshError);
        }
      } else if (originalRequest.url && originalRequest.url.includes('/auth/ping')) {
        // For ping requests, just log the error but don't logout
        console.log('Ping request failed, but not logging out user');
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance; 